// ============================================================================
// PRELIT-CLOSE-001 — Manual Close (UNCLAIMED rows only)
// ============================================================================
// Medium modal. Closure reason (full list — manager picks the right outcome
// category). Notes required, min 20 chars. No EWF trigger fires (no workflow
// instance was attached).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useManualClose } from 'hooks/api/usePreLitigation';
import { useClosureReasons } from 'hooks/api/useClosureReasons';

interface FormState {
  closureReasonId: string;
  notes: string;
}

interface ManualCloseUnclaimedModalProps {
  isOpen: boolean;
  caseId: string;
  caseCode: string;
  customerName: string;
  accountNumber: string;
  onClose: () => void;
  onClosed: () => void;
}

const buildInitial = (): FormState => ({ closureReasonId: '', notes: '' });

const ManualCloseUnclaimedModal: React.FC<ManualCloseUnclaimedModalProps> = ({
  isOpen, caseId, caseCode, customerName, accountNumber, onClose, onClosed,
}) => {
  const [form, setForm] = useState<FormState>(buildInitial());
  const snapRef = useRef('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const close = useManualClose();
  const { data: closureReasons } = useClosureReasons({ page: 1, pageSize: 200 });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    const next = buildInitial();
    setForm(next);
    snapRef.current = JSON.stringify(next);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => snapRef.current && JSON.stringify(form) !== snapRef.current, [form]);

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setFieldErrors(p => { const n = { ...p }; delete n[field as string]; return n; });
    setForm(p => ({ ...p, [field]: value }));
  };

  // Manual close shows the FULL list — manager decides which outcome category
  // applies. The service derives case_status_code (SETTLED/DISMISSED/WITHDRAWN)
  // from the picked reason's outcome_category.
  const closureReasonOptions = useMemo(
    () => (closureReasons ?? []).map((r: any) => ({
      value: String(r.id),
      label: `${r.closureReasonName ?? r.closureCode ?? r.id}${r.outcomeCategory ? ` · ${r.outcomeCategory}` : ''}`,
    })),
    [closureReasons],
  );

  const handleClose = useCallback(() => {
    if (close.loading) return;
    if (isDirty) setDiscardConfirmOpen(true); else onClose();
  }, [isDirty, close.loading, onClose]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!form.closureReasonId) errors.closureReasonId = 'Pick a closure reason.';
    if (!form.notes || form.notes.trim().length < 20) {
      errors.notes = 'Notes are required (min 20 chars).';
    }
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [form]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    try {
      await close.run(caseId, {
        closureReasonId: form.closureReasonId,
        notes: form.notes.trim(),
      });
      notificationService.toast.success(`Case ${caseCode} closed manually.`);
      onClosed();
      onClose();
    } catch (err: any) {
      const status = err?.response?.status;
      const m = err?.response?.data?.message;
      if (status === 409) {
        notificationService.toast.warning(
          m ?? 'This case was just claimed. Refresh and try Escalate or Reassign instead.',
          6000,
        );
      } else {
        notificationService.toast.error(m ?? 'Failed to close case.', 6000);
      }
    }
  }, [close, caseId, caseCode, form, onClosed, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {close.loading && <LoadingOverlay text="Closing case..." />}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Manual Close — {caseCode}</h2>
          <button className="modal-close" onClick={handleClose} disabled={close.loading}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div style={{
            padding: '0.5rem 0.75rem', background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)', borderRadius: '6px',
            marginBottom: '1rem', fontSize: '0.85rem',
          }}>
            Closing the unclaimed pool entry for <strong>{customerName}</strong> ({accountNumber}).
            No workflow instance was started, so this is a direct close.
          </div>

          <div className="form-group" data-field="closureReasonId">
            <SyutSelect
              label={<>Reason for closing <span className="text-danger">*</span></>}
              options={closureReasonOptions}
              value={form.closureReasonId}
              onChange={(v) => setField('closureReasonId', (v as string) || '')}
              error={fieldErrors.closureReasonId}
              placeholder="Pick a closure reason..."
            />
            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
              Outcome category drives case status: FAVOURABLE → SETTLED · UNFAVOURABLE → DISMISSED · NEUTRAL → WITHDRAWN.
            </div>
          </div>

          <div className="form-group" data-field="notes">
            <label className="form-label">Notes <span className="text-danger">*</span> <span style={{ color: 'var(--text-muted)' }}>(min 20 chars)</span></label>
            <textarea
              className={`form-textarea${fieldErrors.notes ? ' form-field-error' : ''}`}
              rows={5}
              maxLength={2000}
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
              placeholder="Why is this unclaimed case being closed manually?"
            />
            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
              {form.notes.length} / 2000
            </div>
            {fieldErrors.notes && <div className="form-error-message">{fieldErrors.notes}</div>}
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={close.loading}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={close.loading}>Close Case</button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Close"
        message={`Close pool entry for case ${caseCode}? This cannot be undone — the case will need to be re-evaluated for eligibility to re-enter the pool.`}
        confirmLabel="Close"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Discard?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

const LoadingOverlay: React.FC<{ text: string }> = ({ text }) => (
  <div style={{
    position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
  }}>
    <div style={{
      width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
      borderTopColor: '#dc2626', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
    }} />
    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>{text}</span>
  </div>
);

export default ManualCloseUnclaimedModal;
